import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import logo from "../../assets/logo.png";
import config from "../Config/config";
import { buildSectionTree, stripHtml } from "./infoShared";

const UserView = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const [expandedDocs, setExpandedDocs] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const docsResponse = await axios.get(`${config.API_BASE_URL}/docs`);
        const docs = docsResponse.data || [];

        const sectionResponses = await Promise.all(
          docs.map((doc) =>
            axios.get(`${config.API_BASE_URL}/docs/${doc.id}/sections`)
          )
        );

        const docsTree = docs.map((doc, index) => ({
          id: doc.id,
          title: doc.title,
          content: doc.description,
          sections: buildSectionTree(sectionResponses[index].data || []),
        }));

        setTree(docsTree);

        const initialExpandedDocs = {};
        docsTree.forEach((doc) => {
          initialExpandedDocs[doc.id] = true;
        });
        setExpandedDocs(initialExpandedDocs);

        if (docsTree.length > 0) {
          setSelected({
            id: docsTree[0].id,
            type: "doc",
            title: docsTree[0].title,
            content: docsTree[0].content,
          });
          setSelectedId(docsTree[0].id);
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filteredTree = useMemo(() => {
    const lower = query.trim().toLowerCase();
    if (!lower) return tree;

    const filterSections = (sections) => {
      return sections
        .map((section) => {
          const titleMatch = (section.title || "").toLowerCase().includes(lower);
          const bodyMatch = stripHtml(section.content || "").toLowerCase().includes(lower);
          const filteredChildren = filterSections(section.subsections || []);

          if (titleMatch || bodyMatch || filteredChildren.length > 0) {
            return {
              ...section,
              subsections: filteredChildren,
            };
          }

          return null;
        })
        .filter(Boolean);
    };

    return tree
      .map((doc) => {
        const titleMatch = (doc.title || "").toLowerCase().includes(lower);
        const bodyMatch = stripHtml(doc.content || "").toLowerCase().includes(lower);
        const filteredSections = filterSections(doc.sections || []);

        if (titleMatch || bodyMatch || filteredSections.length > 0) {
          return {
            ...doc,
            sections: titleMatch || bodyMatch ? doc.sections : filteredSections,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [query, tree]);

  const toggleDoc = (id) => {
    setExpandedDocs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSection = (id) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectDoc = (doc) => {
    setSelected({
      id: doc.id,
      type: "doc",
      title: doc.title,
      content: doc.content,
    });
    setSelectedId(doc.id);
    setMobileNavOpen(false);
  };

  const selectSection = (section) => {
    setSelected({
      id: section.id,
      type: "section",
      title: section.title,
      content: section.content,
    });
    setSelectedId(section.id);
    setMobileNavOpen(false);
  };

  const renderSections = (sections, depth = 0) => {
    return (
      <ul className="space-y-1">
        {sections.map((section) => {
          const hasChildren = section.subsections?.length > 0;
          const isOpen = expandedSections[section.id] ?? false;
          const isActive = selectedId === section.id;

          return (
            <li key={section.id}>
              <div className="flex items-center gap-1">
                {hasChildren ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="h-6 w-6 text-xs text-slate-500 rounded hover:bg-slate-100"
                  >
                    {isOpen ? "▾" : "▸"}
                  </button>
                ) : (
                  <span className="h-6 w-6" />
                )}

                <button
                  type="button"
                  onClick={() => selectSection(section)}
                  className={`flex-1 text-left rounded-lg px-2 py-1.5 text-sm transition-colors ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                  style={{ marginLeft: `${depth * 8}px` }}
                >
                  {section.title || "Untitled Section"}
                </button>
              </div>

              {hasChildren && isOpen && (
                <div className="pl-4 mt-1">{renderSections(section.subsections, depth + 1)}</div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm shadow-sm">
          Loading documentation...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full px-4 py-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-medium shadow-sm text-center">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/50 to-slate-100 flex">
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-80 max-w-[85vw] bg-white border-r border-slate-200 shadow-xl lg:shadow-none transform transition-transform duration-200 ${
          mobileNavOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="px-4 py-4 border-b border-slate-200 flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <img src={logo} alt="Listernaut" className="h-7 w-auto" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Listernaut Docs</p>
              <p className="text-xs text-slate-500">Knowledge Base</p>
            </div>
          </div>

          <div className="px-4 py-3 border-b border-slate-200">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search docs and sections"
              className="w-full rounded-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-500"
            />
          </div>

          <div className="flex-1 overflow-auto p-3 space-y-3">
            {filteredTree.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                No matching docs.
              </div>
            ) : (
              filteredTree.map((doc) => {
                const isOpen = expandedDocs[doc.id] ?? true;
                const isActive = selectedId === doc.id;

                return (
                  <div key={doc.id} className="rounded-xl border border-slate-200 bg-white">
                    <div className="flex items-center px-2 py-2">
                      <button
                        type="button"
                        onClick={() => toggleDoc(doc.id)}
                        className="h-7 w-7 text-sm text-slate-500 rounded hover:bg-slate-100"
                      >
                        {isOpen ? "▾" : "▸"}
                      </button>
                      <button
                        type="button"
                        onClick={() => selectDoc(doc)}
                        className={`flex-1 text-left rounded-lg px-2 py-1.5 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-slate-900 text-white"
                            : "text-slate-800 hover:bg-slate-100"
                        }`}
                      >
                        {doc.title || "Untitled Document"}
                      </button>
                    </div>

                    {isOpen && doc.sections?.length > 0 && (
                      <div className="px-2 pb-2">{renderSections(doc.sections)}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </aside>

      {mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-4 lg:hidden">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 bg-white"
          >
            Browse Docs
          </button>
        </div>

        {selected ? (
          <div className="max-w-5xl mx-auto rounded-2xl border border-slate-200 bg-white/95 shadow-[0_18px_40px_rgba(15,23,42,0.10)] px-5 py-5 sm:px-7 sm:py-6">
            <p className="text-xs uppercase tracking-wide text-slate-500">
              {selected.type === "doc" ? "Document" : "Section"}
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
              {selected.title || "Untitled"}
            </h1>

            {selected.content ? (
              <div
                className="prose prose-sm sm:prose max-w-none text-slate-800 mt-5"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(selected.content),
                }}
              />
            ) : (
              <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-500">
                No content has been added yet.
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-xl mx-auto rounded-2xl border border-slate-200 bg-white/95 shadow-sm px-6 py-8 text-center">
            <h2 className="text-xl font-semibold text-slate-900">Documentation</h2>
            <p className="text-sm text-slate-600 mt-2">
              Pick a document or section from the left to view content.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserView;
